import React from 'react';
import type { FrameStyle, HandStyle, Mecanisme, Numerals } from '../types/clock';
type Props = {
  image: string;
  numerals: Numerals;
  hands: HandStyle;
  frame: FrameStyle;
  texte: string;
  sousTexte: string;
  mecanisme: Mecanisme;
  className?: string;
  label?: string;
};
const ROMAINS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
const HAND_COLORS: Record<HandStyle, string> = {
  noir: '#17150F',
  or: '#C08A3E',
  blanc: '#FFFFFF'
};
const FRAME_COLORS: Record<Exclude<FrameStyle, 'aucun'>, string> = {
  chene: '#C9A272',
  noyer: '#5B3B24'
};
function polar(angleDeg: number, radius: number) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return {
    x: 50 + radius * Math.cos(rad),
    y: 50 + radius * Math.sin(rad)
  };
}
export function ClockPreview({
  image,
  numerals,
  hands,
  frame,
  texte,
  sousTexte,
  mecanisme,
  className = '',
  label = 'Aperçu de votre horloge personnalisée'
}: Props) {
  const handColor = HAND_COLORS[hands];
  const handStroke = hands === 'blanc' ? '#17150F' : 'rgba(255,255,255,0.75)';
  const indices = Array.from({
    length: 12
  }, (_, i) => i + 1);
  return <svg viewBox="0 0 100 100" role="img" aria-label={label} className={`w-full ${className}`} style={{
    filter: 'drop-shadow(0 22px 26px rgba(34,31,26,0.35))'
  }} data-id="element-14" data-mp="3kc0xs14rq6g4">
      <defs data-id="element-15" data-mp="3ub9o8k0fd2xq">
        <clipPath id="dial-clip" data-id="element-16" data-mp="3u7vlvbrrgtqa">
          <circle cx="50" cy="50" r="47" data-id="element-17" data-mp="26pbicbnc2yp" />
        </clipPath>
      </defs>

      {frame !== 'aucun' && <circle cx="50" cy="50" r="48.4" fill="none" stroke={FRAME_COLORS[frame]} strokeWidth="3.2" data-id="element-18" data-mp="74qwj2smucct" />}

      <g clipPath="url(#dial-clip)" data-id="element-19" data-mp="gv27386tnjso">
        <rect x="0" y="0" width="100" height="100" fill="#efe9de" data-id="element-20" data-mp="gu0mac007ixr" />
        <image href={image} x="-3" y="-3" width="106" height="106" preserveAspectRatio="xMidYMid slice" data-id="element-21" data-mp="luewo0p2v4yy" />
      </g>
      <circle cx="50" cy="50" r="47" fill="none" stroke="rgba(23,21,15,0.85)" strokeWidth="0.9" data-id="element-22" data-mp="pte0fhz2ovot" />

      {numerals === 'batons' && indices.map(i => {
      const a = i * 30;
      const outer = polar(a, 43);
      const inner = polar(a, i % 3 === 0 ? 35 : 38);
      return <line key={i} x1={outer.x} y1={outer.y} x2={inner.x} y2={inner.y} stroke="#FFFFFF" strokeWidth={i % 3 === 0 ? 1.8 : 1} strokeLinecap="round" style={{
        filter: 'drop-shadow(0 0 0.8px rgba(0,0,0,0.9))'
      }} data-id="element-23" data-mp="2qxzoui1jqea5" />;
    })}

      {(numerals === 'arabes' || numerals === 'romains') && indices.map(i => {
      const p = polar(i * 30, 39);
      const isRoman = numerals === 'romains';
      return <text key={i} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="central" fill="#FFFFFF" stroke="#17150F" strokeWidth={isRoman ? 0.7 : 0.9} paintOrder="stroke" strokeLinejoin="round" fontFamily={isRoman ? 'Fraunces, Georgia, serif' : 'Inter, sans-serif'} fontWeight="700" fontSize={isRoman ? 6 : 8} data-id="element-24" data-mp="1kblx0ujqs7lf">
              {isRoman ? ROMAINS[i - 1] : i}
            </text>;
    })}

      {texte.trim() && <text x="50" y={sousTexte.trim() ? 66 : 69} textAnchor="middle" fill="#FFFFFF" stroke="#17150F" strokeWidth="0.75" paintOrder="stroke" strokeLinejoin="round" fontFamily="Fraunces, Georgia, serif" fontStyle="italic" fontWeight="600" fontSize="7" data-id="element-25" data-mp="rf39hfblmkji">
          {texte.slice(0, 22)}
        </text>}
      {sousTexte.trim() && <text x="50" y="74" textAnchor="middle" fill="#FFFFFF" stroke="#17150F" strokeWidth="0.6" paintOrder="stroke" strokeLinejoin="round" fontFamily="Inter, sans-serif" fontWeight="600" fontSize="4.2" letterSpacing="0.3" data-id="element-26" data-mp="2crxq9moupoo8">
          {sousTexte.slice(0, 28)}
        </text>}

      <g style={{
      filter: 'drop-shadow(0.6px 0.8px 0.6px rgba(0,0,0,0.35))'
    }} data-id="element-27" data-mp="3uxvyj6nbs48i">
        <g transform="rotate(305 50 50)" data-id="element-28" data-mp="3pyawuogrths3">
          <line x1="50" y1="54" x2="50" y2="27" stroke={handColor} strokeWidth="2.4" strokeLinecap="round" data-id="element-29" data-mp="3l5zvriaanpxy" />
          <line x1="50" y1="54" x2="50" y2="27" stroke={handStroke} strokeWidth="0.4" strokeLinecap="round" opacity="0.5" data-id="element-30" data-mp="3b83ox2oe3h5n" />
        </g>
        <g transform="rotate(60 50 50)" data-id="element-31" data-mp="328gl6a86tz9f">
          <line x1="50" y1="56" x2="50" y2="16" stroke={handColor} strokeWidth="1.7" strokeLinecap="round" data-id="element-32" data-mp="329i0iqw8l7wy" />
        </g>
        {mecanisme === 'trotteuse' && <g transform="rotate(200 50 50)" data-id="element-33" data-mp="1qr2v9avolqyz">
            <line x1="50" y1="60" x2="50" y2="18" stroke="#B4552F" strokeWidth="0.7" strokeLinecap="round" data-id="element-34" data-mp="1vow1g3wzikz9" />
          </g>}
        <circle cx="50" cy="50" r="2.1" fill={handColor} data-id="element-35" data-mp="1318lx3akmkjh" />
        <circle cx="50" cy="50" r="0.7" fill="#F8F3EA" data-id="element-36" data-mp="17za2nhkiy7rs" />
      </g>
    </svg>;
}